import { useCallback, useEffect, useRef } from "react";
import { useLocation } from "react-router";
import {
  createDefaultScrollPosition,
  useScrollStore
} from "@/common/store/scrollStore";
import {
  enableSnapFeedState,
  PREVIEW_FEED_SELECTOR,
  SNAP_ROOT_SELECTOR
} from "@/common/utils/snapFeedUtils";
import { getResumeScrollFrom } from "@/common/utils/feedResume";
import { findScrollPosition, keepScrollTopUntilUserInput } from "@/common/utils/scrollRestoration";
import { useAppStore } from "@/common/store/appStore";
import { getFullscreenEntry } from "@/common/utils/fullscreenHistory";

const getRootElement = (): HTMLElement | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const rootElement = document.querySelector(SNAP_ROOT_SELECTOR);
  return rootElement instanceof HTMLElement ? rootElement : null;
};

const getPreviewFeedElement = (): HTMLElement | null => {
  if (typeof document === "undefined") {
    return null;
  }

  const previewFeed = document.querySelector(PREVIEW_FEED_SELECTOR);
  return previewFeed instanceof HTMLElement ? previewFeed : null;
};

export const useScroll = () => {
  const scrollElement = useScrollStore((state) => state.scrollElement);
  const location = useLocation();
  const resumeScrollFrom = getResumeScrollFrom(location.state);
  const restoredKeyRef = useRef<string | null>(null);
  const closingEntryRef = useRef<{ sourceKey: string } | null>(null);

  const handleScroll = useCallback((event: Event) => {
    if (!scrollElement) {
      return;
    }

    const target = event.target;

    if (!(target instanceof HTMLElement)) {
      return;
    }

    const isRootTarget = target === scrollElement;
    const isFeedTarget = target.matches(PREVIEW_FEED_SELECTOR);

    if (!isRootTarget && !isFeedTarget) {
      return;
    }

    const isRestoringScroll = restoredKeyRef.current !== location.key;
    const isFullScreenPlayerOpen = useAppStore.getState().isFullScreen;

    if (isRestoringScroll || isFullScreenPlayerOpen) {
      return;
    }

    const { scrolls, setScroll } = useScrollStore.getState();
    const previousScroll = scrolls[location.key] ?? createDefaultScrollPosition();

    const nextScroll = {
      root: isRootTarget ? target.scrollTop : previousScroll.root,
      feed: isFeedTarget ? (target.scrollTop <= 0 ? null : target.scrollTop) : previousScroll.feed,
    };

    setScroll(location.key, nextScroll);

  }, [scrollElement, location.key]);

  useEffect(() => {
    if (!scrollElement) {
      return;
    }

    const closingEntry = closingEntryRef.current;
    const { isFullScreen, isMobileFullscreen } = useAppStore.getState();
    const entry = getFullscreenEntry(location);
    const isActiveFullscreenEntry = Boolean(entry) && (isFullScreen || isMobileFullscreen);

    closingEntryRef.current = isActiveFullscreenEntry && entry ? { sourceKey: entry.sourceKey } : null;

    if (isActiveFullscreenEntry && entry) {
      const { scrolls, setScroll } = useScrollStore.getState();

      if (!scrolls[location.key]) {
        const previewFeed = getPreviewFeedElement();

        setScroll(location.key, scrolls[entry.sourceKey] ?? {
          root: scrollElement.scrollTop,
          feed: previewFeed && previewFeed.scrollTop > 0 ? previewFeed.scrollTop : null,
        });
      }

      restoredKeyRef.current = location.key;
      return;
    }

    if (closingEntry?.sourceKey === location.key) {
      restoredKeyRef.current = location.key;
      return;
    }

    let stopRestoring: (() => void) | undefined;

    const markRestored = () => {
      restoredKeyRef.current = location.key;
    };

    const timeoutId = window.setTimeout(() => {
      const { scrolls, setScroll } = useScrollStore.getState();
      const scroll = findScrollPosition(scrolls, location.key, resumeScrollFrom);
      const rootElement = getRootElement();
      const previewFeed = getPreviewFeedElement();

      if (scrolls[location.key] !== scroll) {
        setScroll(location.key, scroll);
      }

      scrollElement.scrollTo(0, scroll.root);

      if (scroll.feed !== null && rootElement && previewFeed) {
        enableSnapFeedState(rootElement);
        stopRestoring = keepScrollTopUntilUserInput(previewFeed, scroll.feed, markRestored);
      } else if (scroll.root > 0) {
        stopRestoring = keepScrollTopUntilUserInput(scrollElement, scroll.root, markRestored);
      } else {
        markRestored();
      }
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      stopRestoring?.();
    };
  }, [location, resumeScrollFrom, scrollElement]);

  useEffect(() => {
    document.addEventListener("scroll", handleScroll, true);

    return () => {
      document.removeEventListener("scroll", handleScroll, true);
    };
  }, [handleScroll]);
};
